/**
 * pdf.js, le moteur du lecteur PDF du web, importé par le navigateur au premier PDF ouvert : ses
 * fichiers sont servis tels quels, hors du bundle (pourquoi et comment : `scripts/build-vendor-assets.js`).
 *
 * Un seul worker pour toute la visite, pas un par document : la bibliothèque et son worker sont lus
 * au même moment, donc de la même version, même si une mise en prod passe pendant que l'élève a l'app
 * ouverte (pdf.js refuse un worker d'une autre version que la sienne).
 */
import type * as Pdfjs from "pdfjs-dist"
import { Linking } from "react-native"

export type PdfjsLib = typeof Pdfjs
export type PdfDocument = Pdfjs.PDFDocumentProxy
export type PdfLoadingTask = Pdfjs.PDFDocumentLoadingTask

export type PdfEngine = { lib: PdfjsLib; worker: Pdfjs.PDFWorker }

const BASE = "/vendor/pdfjs/"

/**
 * Un import natif du navigateur, que Metro laisse tel quel (`@metro-ignore`) — il ne sait pas
 * empaqueter une adresse — comme Vite pour Storybook (`@vite-ignore`).
 */
const importFromUrl = (url: string): Promise<PdfjsLib> => import(/* @metro-ignore */ /* @vite-ignore */ url)

let loading: Promise<PdfEngine> | null = null

/** La bibliothèque et son worker, une fois par visite ; un test passe son propre import. */
export function loadPdfEngine(load: (url: string) => Promise<PdfjsLib> = importFromUrl): Promise<PdfEngine> {
    if (loading != null) return loading
    loading = load(`${BASE}pdf.min.js`)
        .then((lib) => {
            lib.GlobalWorkerOptions.workerSrc = `${BASE}pdf.worker.min.js`
            return { lib, worker: new lib.PDFWorker() }
        })
        .catch((cause: unknown) => {
            // Pas arrivée (réseau coupé, navigateur trop ancien) : le prochain PDF ouvert retentera.
            loading = null
            throw cause
        })
    return loading
}

/**
 * Ouvre un PDF par son adresse. Le fichier arrive d'une traite (`disableRange`) : une lecture par
 * morceaux enverrait un en-tête `Range` que ni S3 ni l'API n'autorisent d'une autre origine, pour des
 * documents de quelques Mo. Les polices de base qu'un PDF léger nomme sans les embarquer viennent de
 * `standard_fonts/` ; `isEvalSupported: false` ne laisse pdf.js compiler aucun code tiré du fichier.
 */
export function openPdf(engine: PdfEngine, uri: string): PdfLoadingTask {
    return engine.lib.getDocument({
        url: uri,
        worker: engine.worker,
        disableRange: true,
        isEvalSupported: false,
        standardFontDataUrl: `${BASE}standard_fonts/`,
    })
}

/** Ouvre le PDF dans un nouvel onglet : le navigateur y sait le lire, l'imprimer ou l'enregistrer. */
export function openPdfInNewTab(uri: string): void {
    void Linking.openURL(uri)
}

/** Le nom d'un PDF téléchargé : le dernier segment de son adresse s'il en est un, sinon `document.pdf`. */
export function pdfFileName(uri: string): string {
    const segment = uri.split(/[?#]/)[0]?.split("/").pop() ?? ""
    let name = segment
    try {
        name = decodeURIComponent(segment)
    } catch {
        // Un segment mal encodé reste tel quel.
    }
    return /\.pdf$/i.test(name) ? name : "document.pdf"
}

/**
 * Télécharge le PDF sous son nom. L'attribut `download` d'un lien est ignoré d'une autre origine :
 * le fichier passe donc par une adresse `blob:` de la page. S'il ne se lit pas d'ici (réseau, origine
 * refusée), il s'ouvre dans un nouvel onglet, où le navigateur sait l'enregistrer.
 */
export async function downloadPdf(uri: string): Promise<void> {
    try {
        const response = await fetch(uri)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const href = URL.createObjectURL(await response.blob())
        const link = document.createElement("a")
        link.href = href
        link.download = pdfFileName(uri)
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(href), 60_000)
    } catch {
        openPdfInNewTab(uri)
    }
}
